import React, { useMemo } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { LocalOffer as TagIcon } from '@mui/icons-material';
import { PACKAGE_TAG_PREFIX, firstTagWithPrefix, slugToLabel } from '../utils/tags';
import type { Prompt, PromptTypeType } from '../../types/backend';
import { promptTypeLabel } from '../constants/promptTypes';

interface PromptOption {
  id: string;
  label: string;
  group: string;
  prompt: Prompt;
}

function buildOptions(prompts: Prompt[]): PromptOption[] {
  const packageMap = new Map<string, Prompt[]>();
  const ungrouped: Prompt[] = [];

  for (const p of prompts) {
    const pkg = firstTagWithPrefix(p.tags);
    if (pkg) {
      const slug = pkg.slice(PACKAGE_TAG_PREFIX.length);
      if (!packageMap.has(slug)) packageMap.set(slug, []);
      packageMap.get(slug)!.push(p);
    } else {
      ungrouped.push(p);
    }
  }

  const options: PromptOption[] = [];

  for (const [slug, pkgPrompts] of packageMap.entries()) {
    const pkgLabel = slugToLabel(slug);
    for (const p of pkgPrompts) {
      options.push({ id: p.id, label: p.title || p.name, group: pkgLabel, prompt: p });
    }
  }

  for (const p of ungrouped) {
    options.push({ id: p.id, label: p.title || p.name, group: 'Other', prompt: p });
  }

  return options;
}

export interface PromptSelectorProps {
  prompts: Prompt[];
  value: string | null;
  onChange: (promptId: string | null) => void;
  typeFilter?: PromptTypeType[];
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  loading?: boolean;
  size?: 'small' | 'medium';
}

const PromptSelector: React.FC<PromptSelectorProps> = ({
  prompts,
  value,
  onChange,
  typeFilter,
  label = 'Prompt',
  placeholder = 'Type to filter...',
  required,
  error,
  helperText,
  disabled,
  loading,
  size = 'small',
}) => {
  const filtered = useMemo(() => {
    if (!typeFilter || typeFilter.length === 0) return prompts;
    return prompts.filter(p => typeFilter.includes(p.type));
  }, [prompts, typeFilter]);

  const options = useMemo(() => buildOptions(filtered), [filtered]);

  const selected = options.find(o => o.id === value) ?? null;

  return (
    <Autocomplete
      options={options}
      groupBy={(o) => o.group}
      getOptionLabel={(o) => o.label}
      value={selected}
      onChange={(_, v) => onChange(v ? v.id : null)}
      disabled={disabled || loading}
      size={size}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={error}
          helperText={helperText}
          InputLabelProps={{ shrink: true }}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        const typeLabel = promptTypeLabel(option.prompt.type);
        const ownerLabel = option.prompt.owner === 'customer' ? 'Customer' : 'Global';
        const version = option.prompt.promptVersion && option.prompt.promptVersion !== 'DRAFT'
          ? `v${option.prompt.promptVersion}`
          : 'Unpublished';
        return (
          <Box component="li" {...props} key={option.id}>
            <Box>
              <Typography variant="body2">{option.label}</Typography>
              <Typography variant="caption" color="text.secondary">
                {typeLabel} · {ownerLabel} · {version}
              </Typography>
            </Box>
          </Box>
        );
      }}
      renderGroup={(params) => (
        <Box key={params.key}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, pt: 1, pb: 0.25 }}>
            <TagIcon sx={{ fontSize: 12, color: 'primary.main', opacity: 0.7 }} />
            <Typography
              variant="caption"
              fontWeight={700}
              color="primary.main"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
              {params.group}
            </Typography>
          </Box>
          {params.children}
        </Box>
      )}
    />
  );
};

export default PromptSelector;
